import {PageDTO} from "app/core/types/pagination.types";
import {ConditionInternalIDs} from "../../../tooth-chart/tooth/tooth.customize.types";
import {TreatmentService} from "./treatment.service";

export class Discipline {
    id:string;
    name:string;
}

export enum TreatmentEntryType {
    Condition=("condition"),
    Treatment=("treatment"),
}

export enum TreatmentComponentAction {
    EDIT=("EDIT"),
    DISABLE=("DISABLE"),
    ENABLE=("ENABLE"),
}

export class TreatmentPageDTO extends PageDTO{
    content: any[];
}
export class GroupType {
    groupLabels: string[];
    groupMap: Map<string, TreatmentModel[]>;
}

export class TreatmentDTO implements TreatmentModel{
    id: string = "";
    internalId:ConditionInternalIDs = ConditionInternalIDs.NOT_ASSIGNED;
    summary : string = "";
    category: string = "";
    description : string = "";
    icon:string = "";
    cost:number = 0;
    treatmentExpires: boolean = false;
    expireDays: number = 0;
    expireHours: number = 0;
    expireMinutes: number = 0;
    clinic: string = "";
    deactivated: boolean = false;
    disciplineId: string = "";
    productCode: string = "";

    static getInstance(): TreatmentDTO {
        return new TreatmentDTO({
            id: "",
            internalId: ConditionInternalIDs.NOT_ASSIGNED,
            summary : "",
            category: "",
            description: "",
            icon: "",
            productCode: "",
        });
    }

    static fromTreatmentModel(model:TreatmentModel):TreatmentDTO{
        return new TreatmentDTO({
            id: model.getID(),
            internalId: model.getInternalID(),
            summary : model.getSummary(),
            category: model.getCategory(),
            description: model.getDescription(),
            icon: model.getIcon(),
            cost: model.getCost(),
            treatmentExpires: model.hasExpirationData(),
            expireDays: model.getExpirationData()[0],
            expireHours: model.getExpirationData()[1],
            expireMinutes: model.getExpirationData()[2],
            clinic: model.getClinicId(),
            deactivated: model.isDisabled(),
            disciplineId: model.getDisciplineID(),
            productCode: model.getProductCode()
        });
    }

    constructor(obj:any) {
        this.id = obj.id;
        this.internalId = obj.internalId;
        this.summary = obj.summary;
        this.category = obj.category;
        this.description = obj.description;
        this.icon = obj.icon;
        this.cost = obj.cost;
        this.treatmentExpires = obj.treatmentExpires;
        this.expireDays = obj.expireDays;
        this.expireHours = obj.expireHours;
        this.expireMinutes = obj.expireMinutes;
        this.clinic = obj.clinic;
        this.deactivated = obj.deactivated;
        this.disciplineId = obj.disciplineId;
        this.productCode = obj.productCode;
    }

    getCategory(): string {
        return this.category;
    }

    getClinicId(): string {
        return this.clinic;
    }

    getCost(): number {
        return +this.cost;
    }

    getDescription(): string {
        return this.description;
    }

    getDisciplineID(): string {
        return this.disciplineId;
    }

    getExpirationData(): number[] {
        return [this.expireDays, this.expireHours, this.expireMinutes];
    }

    getID(): string {
        return this.id;
    }

    getIcon(): string {
        return this.icon;
    }

    getInternalID(): ConditionInternalIDs {
        return this.internalId;
    }

    getSummary(): string {
        return this.summary;
    }

    getType(): TreatmentEntryType {
        return TreatmentEntryType.Treatment;
    }

    hasExpirationData(): boolean {
        return this.treatmentExpires;
    }

    isDisabled(): boolean {
        return this.deactivated;
    }

    setCategory(category: string) {
        this.category = category;
    }

    setClinicId(clinicId: string) {
        this.clinic = clinicId;
    }

    setCost(cost: number) {
        this.cost = +cost;
    }

    setDescription(description: string) {
        this.description = description;
    }

    setDisabled(disabled: boolean) {
        this.deactivated = disabled;
    }

    setDisciplineID(disciplineID: string) {
        this.disciplineId = disciplineID;
    }

    setExpirationData(expireDays: number, expireHours: number, expireMinutes: number) {
        this.expireDays = expireDays;
        this.expireHours = expireHours;
        this.expireMinutes = expireMinutes;
        this.treatmentExpires = expireDays > 0 || expireHours > 0 || expireMinutes > 0;
    }

    setID(id: string) {
        this.id = id;
    }

    setIcon(icon: string) {
        this.icon = icon;
    }

    setInternalID(internalID: ConditionInternalIDs) {
        this.internalId = internalID;
    }

    setSummary(summary: string) {
        this.summary = summary;
    }

    getProductCode(){
        return this.productCode;
    }

    setProductCode(code:string) {
        this.productCode = code;
    }
}

export class Condition implements TreatmentModel {
    id: string;
    internalId: ConditionInternalIDs;
    summary : string;
    category: string;
    description : string;
    icon:string;
    isDeactivated: boolean;
    clinicId: string;
    disciplineId: string;

    static getInstance(): Condition {
        return new Condition({
            id: "",
            internalId: ConditionInternalIDs.NOT_ASSIGNED,
            summary : "",
            category: "",
            description: "",
            icon: "",
            isDeactivated: false,
            clinicId: "",
            disciplineId: ""
        });
    }

    static fromTreatmentModel(model:TreatmentModel):Condition{
        return new Condition({
            id: model.getID(),
            internalId: model.getInternalID(),
            summary : model.getSummary(),
            category: model.getCategory(),
            description: model.getDescription(),
            icon: model.getIcon(),
            isDeactivated: model.isDisabled(),
            clinicId: model.getClinicId(),
            disciplineId: model.getDisciplineID()
        });
    }

    constructor(obj:any) {
        this.id = obj.id;
        this.internalId = obj.internalId;
        this.summary = obj.summary;
        this.category = obj.category;
        this.description = obj.description;
        this.icon = obj.icon;
        this.isDeactivated = obj.isDeactivated;
        this.clinicId = obj.clinicId;
        this.disciplineId = obj.disciplineId;
    }

    getCategory(): string {
        return this.category;
    }

    getClinicId(): string {
        return this.clinicId;
    }

    getCost(): number {
        return 0;
    }

    getDescription(): string {
        return this.description;
    }

    getDisciplineID(): string {
        return this.disciplineId;
    }

    getExpirationData(): number[] {
        return [];
    }

    getID(): string {
        return this.id;
    }

    getIcon(): string {
        return this.icon;
    }

    getInternalID(): ConditionInternalIDs {
        return this.internalId;
    }

    getSummary(): string {
        return this.summary;
    }

    getType(): TreatmentEntryType {
        return TreatmentEntryType.Condition;
    }

    hasExpirationData(): boolean {
        return false;
    }

    isDisabled(): boolean {
        return this.isDeactivated;
    }

    setCategory(category: string) {
        this.category = category;
    }

    setClinicId(clinicId: string) {
        this.clinicId = clinicId;
    }

    setCost(cost: number) {
        //nothing to do
    }

    setDescription(description: string) {
        this.description = description;
    }

    setDisabled(disabled: boolean) {
        this.isDeactivated = disabled;
    }

    setDisciplineID(disciplineID: string) {
        this.disciplineId = disciplineID;
    }

    setExpirationData(expireDays: number, expireHours: number, expireMinutes: number) {
        // nothing to do
    }

    setID(id: string) {
        this.id = id;
    }

    setIcon(icon: string) {
        this.icon = icon;
    }

    setInternalID(internalID: ConditionInternalIDs) {
        this.internalId = internalID;
    }

    setSummary(summary: string) {
        this.summary = summary;
    }

    getProductCode(){
        return '';
    }

    setProductCode(code:string) {
    }
}

export interface TreatmentModel {
    getID():string;
    setID(id:string);
    getInternalID():ConditionInternalIDs;
    setInternalID(internalID:ConditionInternalIDs);
    getSummary():string;
    setSummary(summary:string);
    getCategory():string;
    setCategory(category:string);
    getDescription():string;
    setDescription(description:string);
    getIcon():string;
    setIcon(icon:string);
    isDisabled():boolean;
    setDisabled(disabled:boolean);
    getClinicId():string;
    setClinicId(clinicId:string);
    getDisciplineID():string;
    setDisciplineID(disciplineID:string);
    getCost():number;
    setCost(cost:number);
    hasExpirationData():boolean;
    getExpirationData():number[];
    setExpirationData(expireDays:number, expireHours:number, expireMinutes:number);
    getType():TreatmentEntryType;
    getProductCode():string;
    setProductCode(code:string);
}

export const foreignWorkInternalIDs:string[] = [
    "SURFACE_COR_FILL_FOREIGN", "CROWN_PIVOT_RECONSTRUCTION", "CROWN_ZIRCONIUM",
    "CROWN_METAL", "CROWN_INLINEONE", "CROWN_ACRYL", "CROWN_TEMPORARY", "PIVOT_ZIRCON",
    "PIVOT_METAL", "PROTEZA_ACRYL", "PROTEZA_METALICA", "IMPLANT", "ROOT_FILL"
];

export const stoma_icon_list_conditions = [
    'condition/Abces.png',
    'condition/Afta.png',
    'condition/BleedingGums.png',
    'condition/default.png',
    'condition/gingivita.png',
    'condition/Hole.png',
    'condition/Infected_pulp.png',
    'condition/mobility.png',
    'condition/pain.png',
    'condition/Surface_decay.png',
    'condition/Pit_fisure.png',
    'condition/Root_decay.png',
];
export const stoma_icon_list_treatments = [

    'treatment/Normal.png',
    'treatment/hiperestesia.png',
    'treatment/abces_drain.png',
    'treatment/amalgam_filling.png',
    'treatment/Anesthesia.png',
    'treatment/AntisepticPad.png',
    'treatment/ApicalResection.png',
    'treatment/biopsy.png',
    'treatment/Bracelet.png',
    'treatment/Bridge.png',
    'treatment/composite_filling.png',
    'treatment/consult.png',
    'treatment/Crown.png',
    'treatment/default.png',
    'treatment/Detartraj.png',
    'treatment/Extraction.png',
    'treatment/Fila_ortodontica.png',
    'treatment/gold_crown.png',
    'treatment/Gutiera.png',
    'treatment/implant.png',
    'treatment/implant_crown.png',
    'treatment/Laser.png',
    'treatment/Obturatie_radiculara.png',
    'treatment/PeriodicConsult.png',
    'treatment/Pills.png',
    'treatment/plomba.png',
    'treatment/Prophylaxis.png',
    'treatment/proteza_scheletata_acril.png',
    'treatment/proteza_scheletata_metal.png',
    'treatment/satin_less_steel_crown.png',
    'treatment/Space_mantainer.png',
    'treatment/suture.png',
];

export const categoryLabels = {
    "treatments": [
        "Stomatologie generală",
        "Endodonție",
        "Ortodonție",
        "Parodontologie",
        "Pedodonție",
        "Protetică dentară",
        "Chirurgie maxilo-facială",
        "Chirurgie dento-alveolară",
    ],

    "conditions": [
        "Stomatologie generală",
        "Endodonție",
        "Ortodonție",
        "Parodontologie",
        "Pedodonție",
        "Protetică dentară",
        "Chirurgie maxilo-facială",
        "Chirurgie dento-alveolară",
    ],
}
